import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  TextField,
  Button,
  Grid,
  Divider,
  InputAdornment,
} from '@mui/material';
import { createEstimate } from '../../services/estimateService';
import type { OfferStandalone } from '../../services/offerStandaloneService';
import { useAuth } from '../../contexts/AuthContext';
import { FieldLabel, multilineFieldSx, sectionTitleSx, textFieldSx } from './easyEstimateFormFields';

const DEFAULT_CONTRACTOR_NAME = 'Unitresco sp. z o. o.';
const DEFAULT_CONTRACTOR_ADDRESS = 'ul. Żurawia 71, 15-540 Białystok';

export type AddEstimateOfferDrawerProps = {
  open: boolean;
  onClose: () => void;
  offer: OfferStandalone | null;
  offerId: string;
  displayOfferLabel: string;
  onSuccess: (estimateId: number) => void;
  onError: (message: string) => void;
};

type FormState = {
  headerName: string;
  preparedAt: string;
  objectName: string;
  estimateLocation: string;
  branza: string;
  client: string;
  clientAddress: string;
  contractorName: string;
  contractorAddress: string;
  preparer: string;
  reviewer: string;
  kp: string;
  kz: string;
  z: string;
  vat: string;
  rg: string;
};

function emptyForm(): FormState {
  const today = new Date().toISOString().slice(0, 10);
  return {
    headerName: 'Kosztorys ofertowy',
    preparedAt: today,
    objectName: '',
    estimateLocation: '',
    branza: '',
    client: '',
    clientAddress: '',
    contractorName: DEFAULT_CONTRACTOR_NAME,
    contractorAddress: DEFAULT_CONTRACTOR_ADDRESS,
    preparer: '',
    reviewer: '',
    kp: '65',
    kz: '5',
    z: '20',
    vat: '23',
    rg: '45',
  };
}

function parseNum(s: string): number {
  const n = parseFloat(String(s).replace(',', '.').replace(/%/g, '').trim());
  return Number.isFinite(n) ? n : 0;
}

const AddEstimateOfferDrawer: React.FC<AddEstimateOfferDrawerProps> = ({
  open,
  onClose,
  offer,
  offerId,
  displayOfferLabel,
  onSuccess,
  onError,
}) => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = React.useState(0);
  const [form, setForm] = React.useState<FormState>(emptyForm);
  const [saving, setSaving] = React.useState(false);
  const [showErrors, setShowErrors] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setTabValue(0);
    setShowErrors(false);
    const base = emptyForm();
    if (offer) {
      base.objectName = offer.order_name || '';
      base.estimateLocation = offer.location || '';
      base.client = offer.client_name || '';
      base.clientAddress = offer.client_address || '';
    }
    const preparerName = user?.display_name?.trim() || user?.email || '';
    base.preparer = preparerName;
    setForm(base);
  }, [open, offer, user?.display_name, user?.email]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const handleSubmit = async () => {
    if (!offerId) return;
    const name = form.headerName.trim();
    const object_name = form.objectName.trim();
    const client = form.client.trim();
    if (!name || !object_name || !client) {
      setShowErrors(true);
      onError('Uzupełnij wymagane pola: nagłówek, nazwa obiektu, zamawiający.');
      setTabValue(object_name ? (client ? 0 : 1) : 0);
      return;
    }
    setShowErrors(false);
    setSaving(true);
    try {
      const created = await createEstimate({
        name,
        object_name,
        client,
        estimate_location: form.estimateLocation.trim() || undefined,
        branza: form.branza.trim().slice(0, 100) || undefined,
        client_address: form.clientAddress.trim() || undefined,
        contractor_name: form.contractorName.trim() || undefined,
        contractor_address: form.contractorAddress.trim() || undefined,
        preparer: form.preparer.trim() || '—',
        reviewer: form.reviewer.trim() || undefined,
        markup_kp: parseNum(form.kp),
        markup_kz: parseNum(form.kz),
        markup_z: parseNum(form.z),
        vat: parseNum(form.vat),
        rg: parseNum(form.rg),
        prepared_at: form.preparedAt,
        standalone_offer_id: offerId,
      });
      onSuccess(created.id);
      onClose();
    } catch {
      onError('Nie udało się utworzyć kosztorysu.');
    } finally {
      setSaving(false);
    }
  };

  const sectionTitle = (label: string) => <Typography sx={sectionTitleSx}>{label}</Typography>;

  return (
    <Drawer anchor="right" open={open} onClose={() => !saving && onClose()} PaperProps={{ sx: { width: 680, maxWidth: '100vw', borderRadius: 0 } }}>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#fff' }}>
        <Box sx={{ pl: 4, pr: 4, pt: '22px', pb: 0, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '18px', color: '#2D2D32' }}>
              Nowy kosztorys
            </Typography>
            {displayOfferLabel ? (
              <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#5F6368', mt: 0.5 }}>
                Oferta {displayOfferLabel}
              </Typography>
            ) : null}
          </Box>
          <IconButton onClick={() => !saving && onClose()} size="small" sx={{ mt: -0.5 }}>
            <Box component="img" src="/assets/close.svg" alt="Zamknij" sx={{ width: 13, height: 13 }} />
          </IconButton>
        </Box>

        <Box sx={{ px: 4, mt: '28px' }}>
          <Tabs
            value={tabValue}
            onChange={(_, v) => setTabValue(v)}
            variant="standard"
            sx={{
              position: 'relative',
              '&::after': { content: '""', position: 'absolute', left: 0, right: 0, bottom: 0, height: '1px', bgcolor: '#EDEDED' },
              '& .MuiTabs-flexContainer': { justifyContent: 'space-between' },
            }}
            TabIndicatorProps={{ sx: { height: 2, bgcolor: '#000000' } }}
          >
            {['Ogólne', 'Dane', 'Narzuty'].map((label) => (
              <Tab
                key={label}
                label={label}
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontWeight: 400,
                  fontSize: '16px',
                  textTransform: 'none',
                  color: '#020242',
                  minWidth: 'auto',
                  px: 0,
                  '&.Mui-selected': { fontWeight: 500, color: '#020242' },
                }}
              />
            ))}
          </Tabs>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', px: 4, py: 3 }}>
          {tabValue === 0 && (
            <Grid container rowSpacing={1.5} columnSpacing={3}>
              <Grid item xs={12}>
                {sectionTitle('Ogólne')}
              </Grid>
              <Grid item xs={12}>
                <FieldLabel text="Nagłówek kosztorysu" required />
                <TextField
                  fullWidth
                  required
                  variant="outlined"
                  size="small"
                  value={form.headerName}
                  onChange={(e) => update('headerName', e.target.value)}
                  sx={textFieldSx}
                  error={showErrors && !form.headerName.trim()}
                  helperText={showErrors && !form.headerName.trim() ? 'To pole jest wymagane' : undefined}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FieldLabel text="Data opracowania" />
                <TextField
                  type="date"
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={form.preparedAt}
                  onChange={(e) => update('preparedAt', e.target.value)}
                  sx={{
                    ...textFieldSx,
                    '& .MuiInputBase-input': {
                      padding: '8px 12px',
                      boxSizing: 'border-box',
                      fontFamily: 'Roboto, sans-serif',
                      fontSize: '16px',
                      color: form.preparedAt ? '#2D2D32' : '#B9B9B9',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FieldLabel text="Nazwa obiektu (robót)" required />
                <TextField
                  fullWidth
                  required
                  variant="outlined"
                  size="small"
                  value={form.objectName}
                  onChange={(e) => update('objectName', e.target.value)}
                  sx={textFieldSx}
                  error={showErrors && !form.objectName.trim()}
                  helperText={showErrors && !form.objectName.trim() ? 'To pole jest wymagane' : undefined}
                />
              </Grid>
              <Grid item xs={12}>
                <FieldLabel text="Lokalizacja" />
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={form.estimateLocation}
                  onChange={(e) => update('estimateLocation', e.target.value)}
                  sx={textFieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <FieldLabel text="Branża" />
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={form.branza}
                  onChange={(e) => update('branza', e.target.value.slice(0, 100))}
                  inputProps={{ maxLength: 100 }}
                  sx={textFieldSx}
                />
              </Grid>
            </Grid>
          )}

          {tabValue === 1 && (
            <Grid container rowSpacing={1.5} columnSpacing={3}>
              <Grid item xs={12}>
                {sectionTitle('Dane')}
              </Grid>
              <Grid item xs={12}>
                <FieldLabel text="Zamawiający" required />
                <TextField
                  fullWidth
                  required
                  variant="outlined"
                  size="small"
                  value={form.client}
                  onChange={(e) => update('client', e.target.value)}
                  sx={textFieldSx}
                  error={showErrors && !form.client.trim()}
                  helperText={showErrors && !form.client.trim() ? 'To pole jest wymagane' : undefined}
                />
              </Grid>
              <Grid item xs={12}>
                <FieldLabel text="Adres zamawiającego" />
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  variant="outlined"
                  size="small"
                  value={form.clientAddress}
                  onChange={(e) => update('clientAddress', e.target.value)}
                  sx={multilineFieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FieldLabel text="Wykonawca" />
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={form.contractorName}
                  onChange={(e) => update('contractorName', e.target.value)}
                  sx={textFieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FieldLabel text="Adres wykonawcy" />
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={form.contractorAddress}
                  onChange={(e) => update('contractorAddress', e.target.value)}
                  sx={textFieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FieldLabel text="Kosztorysant" />
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={form.preparer}
                  onChange={(e) => update('preparer', e.target.value)}
                  sx={textFieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FieldLabel text="Sprawdzający" />
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={form.reviewer}
                  onChange={(e) => update('reviewer', e.target.value)}
                  sx={textFieldSx}
                />
              </Grid>
            </Grid>
          )}

          {tabValue === 2 && (
            <Grid container rowSpacing={1.5} columnSpacing={3}>
              <Grid item xs={12}>
                {sectionTitle('Narzuty')}
              </Grid>
              <Grid item xs={12} sm={6}>
                <FieldLabel text="Koszty pośrednie (Kp)" />
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={form.kp}
                  onChange={(e) => update('kp', e.target.value)}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                  sx={textFieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FieldLabel text="Koszty zakupu (Kz)" />
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={form.kz}
                  onChange={(e) => update('kz', e.target.value)}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                  sx={textFieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FieldLabel text="Zysk (Z)" />
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={form.z}
                  onChange={(e) => update('z', e.target.value)}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                  sx={textFieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FieldLabel text="VAT" />
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={form.vat}
                  onChange={(e) => update('vat', e.target.value)}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                  sx={textFieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FieldLabel text="Roboczogodzina" />
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={form.rg}
                  onChange={(e) => update('rg', e.target.value)}
                  InputProps={{ endAdornment: <InputAdornment position="end">zł</InputAdornment> }}
                  sx={textFieldSx}
                />
              </Grid>
            </Grid>
          )}
        </Box>

        <Divider />
        <Box sx={{ px: 4, py: 2, display: 'flex', justifyContent: 'flex-end', gap: 2, flexShrink: 0 }}>
          <Button variant="outlined" onClick={() => !saving && onClose()} disabled={saving} sx={{ textTransform: 'none' }}>
            Anuluj
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              backgroundColor: '#4040DB',
              '&:hover': { backgroundColor: '#1F1FB0' },
            }}
          >
            {saving ? 'Tworzenie…' : 'Utwórz kosztorys'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default AddEstimateOfferDrawer;
